import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import translationKeys from '@translationKeys'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusNotFreeUser } from '@modules/auth/types'
import ContactsOverviewCardStateFull from '@modules/contacts/components/ContactsOverviewCardStateFull'
import { Company, CompanyHistoryProfile, ProfileType } from '@modules/search/types'
import { Box } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import { useQuery } from '@tanstack/react-query'

import searchApi from '../../search/api'
import { EventInfo } from '../EventInfo'
import { ProfileElements, SectionProps } from '../ProfileDetails'
import useFetchProfile from '../hooks/useFetchProfile'
import {
  CompanyCardInner,
  CompanyCardName,
  CompanyCardTitle,
  CompanyCardTitleWrap,
  CompanyCardWrap,
  CompanyCardWrapper,
  CompanyProfileButton,
  CompanyProfileButtonWrapper,
  SkeletonWrap,
  StyledProfileLogo,
} from '../index.styles'

const CompanyCardConfig = [
  {
    subSections: [
      {
        fieldName: 'description',
        shouldParse: true,
        truncateToLines: 5,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_MAIN_SECTOR',
        fieldName: 'sector',
        noBottomMargin: true,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_SUBSECTOR',
        fieldName: 'subSectors',
        isList: true,
        noBottomMargin: true,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_CARD_THERAPEUTIC_AREA',
        fieldName: 'primaryTherapeuticAreas',
        isList: true,
        noBottomMargin: true,
      },
    ],
  },
  {
    subSections: [
      {
        subTitle: 'COMPANY_PROFILE_SECTION_CONTACTS',
        joinedFieldsKey: 'fullAddress',
        noBottomMargin: true,
        joinedFieldsConfig: [
          {
            fieldName: 'addressLine1',
            joinSymbol: '',
            isNewLine: true,
          },
          {
            fieldName: 'addressLine2',
            joinSymbol: '',
          },
          {
            fieldName: 'zip',
            joinSymbol: ', ',
          },
          {
            fieldName: 'city',
            joinSymbol: ', ',
          },
          {
            fieldName: 'country',
            joinSymbol: ', ',
          },
          {
            fieldName: 'state',
            joinSymbol: ', ',
          },
          {
            fieldName: 'profileData.phone',
            joinSymbol: '',
            isBold: true,
            isNewLine: true,
          },
        ],
      },
    ],
  },
  {
    subSections: [
      {
        fieldName: 'profileData.email',
        isEmail: true,
        noBottomMargin: true,
      },
    ],
  },
  {
    subSections: [
      {
        fieldName: 'website',
        isLink: true,
        noBottomMargin: true,
        id: 'profiles-company_url',
      },
    ],
  },
]

const CompanyCard = ({ companyId }: { companyId: number }) => {
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const isNotFreeUser = useMemo(() => isP1PlusNotFreeUser(user?.subscriptionLevel || 0), [user])
  const navigate = useNavigate()

  const {
    data: fullCompanyProfile,
    isLoading,
    isError,
  } = useFetchProfile({ type: ProfileType.company, id: companyId, disabled: true })

  const { data: attendeesCounts } = useQuery({
    queryKey: [searchApi.getAttendeesCount.key, companyId],
    queryFn: () => searchApi.getAttendeesCount.fn({ companyId }),
  })

  const { latest: companyData = {} as Company } = fullCompanyProfile || ({} as CompanyHistoryProfile)

  const handleCompanyProfile = () => {
    navigate(`/plus/profile/${companyId}/company`)
  }

  return (
    <CompanyCardWrap>
      <CompanyCardInner>
        {isError && <div>{t(translationKeys.FETCHING_ERROR)}</div>}
        {isLoading && (
          <>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
            <SkeletonWrap>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </SkeletonWrap>
          </>
        )}
        {companyData && (
          <CompanyCardWrapper>
            <CompanyCardTitleWrap>
              <StyledProfileLogo
                profileData={companyData}
                profileType={ProfileType.company}
                width={40}
                defaultLogoSize={25}
                circleView
              />
              <CompanyCardTitle variant="h3">{t(translationKeys.COMPANY_CARD_OVERVIEW)}</CompanyCardTitle>
            </CompanyCardTitleWrap>
            <CompanyCardName variant="h2" onClick={handleCompanyProfile}>
              {companyData?.name}
            </CompanyCardName>
            <EventInfo
              futureEventsCount={attendeesCounts?.events?.future ?? 3}
              pastEventsCount={attendeesCounts?.events?.past}
              delegatesCount={attendeesCounts?.count ?? 1}
              companyId={companyId}
            />

            <Box>
              <ContactsOverviewCardStateFull profile={companyData} />
            </Box>

            <ProfileElements
              profileSectionConfig={CompanyCardConfig as SectionProps[]}
              profileData={companyData}
              profileId={companyId}
              profileAreaName="companyCard"
              hasAccessToAlerts={isNotFreeUser}
              noMargin
            />

            <CompanyProfileButtonWrapper>
              <CompanyProfileButton onClick={handleCompanyProfile} variant="contained">
                {t('COMPANY_CARD_PROFILE_BUTTON')}
              </CompanyProfileButton>
            </CompanyProfileButtonWrapper>
          </CompanyCardWrapper>
        )}
      </CompanyCardInner>
    </CompanyCardWrap>
  )
}

export default CompanyCard
